import React, {FormEvent, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import Community from '../../domain/community'
import CommunityStatus from '../../domain/communityStatus'
import {INACTIVE} from '../../domain/communityStatuses'
import {LICENSED} from '../../domain/communityTypes'
import CountryCode from '../../domain/countryCode'
import LanguageTag from '../../domain/languageTag'
import Name from '../../domain/name'
import StateCode from '../../domain/stateCode'
import TzIdentifier from '../../domain/tzIdentifier'
import ZipPill from '../../domain/zipPill'
import useUpdateCommunity from '../../hooks/useUpdateCommunity'
import CommunityStatusToggle from '../CommunityStatusToggle'
import CountrySelect from '../CountrySelect'
import Divider from '../Divider'
import EthnicityToggle from '../EthnicityToggle'
import FlatButton from '../FlatButton'
import GenderToggle from '../GenderToggle'
import LocaleSelect, {getLanguageTagByCountryCode} from '../LocaleSelect'
import ReadOnlyField from '../ReadOnlyField'
import StateSelect from '../StateSelect'
import TextInput from '../TextInput/AdminTextInput'
import TimeZoneSelect from '../TimeZoneSelect'
import ZipCodeInput from '../ZipCodeInput'

type Props = Readonly<{
  busy?: boolean
  community: Community
  onCancel: () => void
  onError?: (error: Error) => void
  onSuccess?: (message: string) => void
  onValidationFailure: (validationErrorCount: number) => void
  zips: ReadonlyArray<ZipPill>
}>

function EditCommunityForm({
  busy = false,
  community,
  onCancel,
  onError,
  onSuccess,
  onValidationFailure,
  zips: initialZips
}: Props) {
  const intl = useIntl()
  const {updateCommunity} = useUpdateCommunity({
    onSuccess: onUpdateSuccess,
    onError
  })

  const formId = 'edit-community-form'

  const [name, setName] = useState<string>(community.name)
  const [nameErrorMessage, setNameErrorMessage] = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState<CountryCode | null>(
    community.country
  )
  const [countryCodeErrorMessage, setCountryCodeErrorMessage] = useState<
    string | null
  >(null)
  const [stateCode, setStateCode] = useState<StateCode | null>(community.state)

  const [languageTag, setLanguageTag] = useState<LanguageTag | null>(
    community.locale
  )
  const [languageTagErrorMessage, setLanguageTagErrorMessage] = useState<
    string | null
  >(null)
  const [tzIdentifier, setTzIdentifier] = useState<TzIdentifier | null>(
    community.timeZone
  )
  const [tzIdentifierErrorMessage, setTzIdentifierErrorMessage] = useState<
    string | null
  >(null)
  const [zips, setZips] = useState<ReadonlyArray<ZipPill>>(initialZips)
  const [ethnicityEnabled, setEthnicityEnabled] = useState(
    community.ethnicityEnabled
  )
  const [genderEnabled, setGenderEnabled] = useState(community.genderEnabled)
  const [status, setStatus] = useState<CommunityStatus>(
    community.status || INACTIVE
  )

  function clearNameValidationsErrors() {
    setNameErrorMessage(null)
    onValidationFailure(0)
  }

  function clearCountryValidationErrors() {
    setCountryCodeErrorMessage(null)
    onValidationFailure(0)
  }

  function clearLanguageValidationErrors() {
    setLanguageTagErrorMessage(null)
    onValidationFailure(0)
  }

  function clearTimeZoneValidationErrors() {
    setTzIdentifierErrorMessage(null)
    onValidationFailure(0)
  }

  function handleFormSubmission() {
    let valid = true
    let validationErrorCounter = 0

    if (!Name.isValid(name)) {
      setNameErrorMessage(
        intl.formatMessage({id: 'admin.communities.name.validationMessage'})
      )
      validationErrorCounter++
      valid = false
    }

    if (!CountryCode.isValid(countryCode)) {
      setCountryCodeErrorMessage(
        intl.formatMessage({
          id: 'admin.communities.country.validationMessage'
        })
      )
      validationErrorCounter++
      valid = false
    }

    if (!LanguageTag.isValid(languageTag)) {
      setLanguageTagErrorMessage(
        intl.formatMessage({
          id: 'admin.communities.language.validationMessage'
        })
      )
      validationErrorCounter++
      valid = false
    }

    if (!TzIdentifier.isValid(tzIdentifier)) {
      setTzIdentifierErrorMessage(
        intl.formatMessage({
          id: 'admin.communities.timeZone.validationMessage'
        })
      )
      validationErrorCounter++
      valid = false
    }

    if (valid) {
      updateCommunity(
        Community.parse({
          id: community.id,
          type: LICENSED,
          status,
          name,
          state: stateCode,
          country: countryCode,
          currency: community.currency,
          locale: languageTag,
          timeZone: tzIdentifier,
          zips: zips.map((zip: ZipPill) => zip.code.trim()),
          ethnicityEnabled,
          genderEnabled,
          currentEventId: community.currentEventId
        })
      )
    } else {
      onValidationFailure(validationErrorCounter)
    }
  }

  function onUpdateSuccess() {
    onSuccess?.('Community updated')
  }

  return (
    <form
      aria-label={intl.formatMessage({
        id: 'admin.communities.edit.formLabel'
      })}
      id={formId}
      noValidate
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        handleFormSubmission()
      }}
    >
      <div className="rounded bg-white">
        <TextInput
          autoCapitalize="words"
          errorMessage={nameErrorMessage}
          id="community-name"
          inputMode="text"
          label={intl.formatMessage({id: 'admin.communities.name.label'})}
          onChange={event => {
            setName(event.target.value)
            clearNameValidationsErrors()
          }}
          required
          type="text"
          value={name}
        />
        <Divider />
        <CountrySelect
          errorMessage={countryCodeErrorMessage}
          onChange={selectedCountryCode => {
            setCountryCode(selectedCountryCode)
            if (selectedCountryCode !== countryCode) {
              setStateCode(null)
            }
            setLanguageTag(getLanguageTagByCountryCode(selectedCountryCode))
            clearCountryValidationErrors()
            clearLanguageValidationErrors()
          }}
          value={countryCode}
        />
        <Divider />
        <StateSelect
          countryCode={countryCode}
          onChange={setStateCode}
          value={stateCode}
        />
        <Divider />
        <ReadOnlyField
          name="currency"
          label="admin.communities.currency.label"
          value={community.currency}
        />
        <Divider />
        <LocaleSelect
          errorMessage={languageTagErrorMessage}
          onChange={selectedLanguageTag => {
            setLanguageTag(selectedLanguageTag)
            clearLanguageValidationErrors()
          }}
          value={languageTag}
        />
        <Divider />
        <TimeZoneSelect
          errorMessage={tzIdentifierErrorMessage}
          countryCode={countryCode}
          onChange={selectedTzIdentifier => {
            setTzIdentifier(selectedTzIdentifier)
            clearTimeZoneValidationErrors()
          }}
          value={tzIdentifier}
        />
        <Divider />
        <ZipCodeInput
          zips={zips}
          onDelete={deletedZip =>
            setZips(zips.filter(zip => zip.code !== deletedZip.code))
          }
          onAdd={newZips =>
            setZips(
              zips
                .concat(newZips)
                .filter((zip, index, array) => array.indexOf(zip) === index)
            )
          }
          onSort={() => {
            setZips([...zips].sort((a, b) => a.code.localeCompare(b.code)))
          }}
        />
        <Divider />
        <EthnicityToggle
          checked={ethnicityEnabled}
          onChange={setEthnicityEnabled}
        />
        <Divider />
        <GenderToggle checked={genderEnabled} onChange={setGenderEnabled} />
        <Divider />
        <CommunityStatusToggle status={status} onChange={setStatus} />
      </div>
      <div className="mx-auto mt-2 flex w-fit space-x-4 md:mt-4 lg:mt-6 xl:mb-8">
        <FlatButton
          aria-label={intl.formatMessage({
            id: 'general.cancel'
          })}
          form={formId}
          type="button"
          variant="danger"
          onClick={onCancel}
        >
          <FormattedMessage id="general.cancel" />
        </FlatButton>
        <FlatButton
          aria-label={intl.formatMessage({
            id: 'general.save'
          })}
          disabled={busy}
          id="add-community-button"
          form={formId}
          type="submit"
          variant="success"
        >
          <FormattedMessage id="general.save" />
        </FlatButton>
      </div>
    </form>
  )
}

export default EditCommunityForm
